import React, { useEffect, useState } from "react";
import { Save, Mail, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, fmtErr } from "../../lib/api";
import LanguageSwitcher from "../../components/LanguageSwitcher";

const TONES = ["professionnel", "chaleureux", "direct", "commercial", "formel"];
const LENGTHS = ["courte", "moyenne", "détaillée"];

export default function Parametres() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [mailboxes, setMailboxes] = useState(null);
  const [useExp, setUseExp] = useState("");
  const [forbidExp, setForbidExp] = useState("");
  const [busy, setBusy] = useState(false);
  const toneLabels = t("parametres.tones");
  const lengthLabels = t("parametres.lengths");

  const load = () => {
    api.get("/settings").then((r) => {
      setSettings(r.data.ai_settings);
      setUseExp((r.data.ai_settings.use_expressions || []).join(", "));
      setForbidExp((r.data.ai_settings.forbidden_expressions || []).join(", "));
    }).catch(() => {});
    api.get("/mailboxes").then((r) => setMailboxes(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/settings/ai-style", {
        ...settings,
        use_expressions: useExp.split(",").map((s) => s.trim()).filter(Boolean),
        forbidden_expressions: forbidExp.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(t("parametres.saved"));
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("connected");
    const err = new URLSearchParams(window.location.search).get("oauth_error");
    if (p) toast.success(p === "gmail" ? t("parametres.connectedGmail") : t("parametres.connectedOutlook"));
    if (err) toast.error(t("parametres.oauthFailed"));
  }, []);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const real = (mailboxes?.mailboxes || []).filter((m) => m.provider !== "demo");
      if (real.length === 0) { toast.info(t("parametres.syncNone2")); return; }
      for (const mb of real) {
        const { data } = await api.post(`/mailboxes/${mb.mailbox_id}/sync`);
        toast.success(data.new > 0 ? t("parametres.syncNew", { count: data.new }) : t("parametres.syncNone"));
      }
      load();
    } catch (e) { toast.error(fmtErr(e, t("parametres.syncFail"))); }
    setSyncing(false);
  };

  const disconnect = async (mb) => {
    await api.delete(`/mailboxes/${mb.mailbox_id}`);
    toast.success(t("parametres.disconnected"));
    load();
  };

  // OAuth hors iframe, en 2 étapes : init authentifiée (cookies) → URL Microsoft signée → popup
  const connectOutlook = async () => {
    let url;
    try {
      const { data } = await api.post("/mailboxes/oauth/outlook/init");
      url = data.url;
    } catch (e) {
      toast.error(fmtErr(e, t("parametres.syncFail")));
      return;
    }
    const popup = window.open(url, "elvarify-outlook-oauth", "width=620,height=720,menubar=no,toolbar=no");
    if (!popup) {
      window.open(url, "_blank", "noopener"); // fallback : nouvel onglet top-level
      return;
    }
    const timer = setInterval(() => {
      try {
        if (popup.location.origin === window.location.origin &&
            popup.location.search.includes("connected=outlook")) {
          popup.close();
        }
      } catch {}
      if (popup.closed) {
        clearInterval(timer);
        api.get("/mailboxes").then((r) => {
          const outlook = r.data.mailboxes.find((m) => m.provider === "outlook");
          if (outlook) {
            toast.success(t("parametres.connectedOutlook"));
            load();
          }
        }).catch(() => {});
      }
    }, 700);
  };

  if (!settings) return <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="skeleton-shimmer h-32 rounded-2xl" />)}</div>;

  const field = "w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-electric focus:ring-2 focus:ring-electric/20 transition-shadow";

  return (
    <div className="space-y-8 max-w-3xl" data-testid="parametres-page">
      <div>
        <h1 className="font-display font-black text-3xl tracking-tighter">{t("parametres.title")}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t("parametres.subtitle")}</p>
      </div>

      <section className="rounded-2xl border border-border bg-card p-7" data-testid="language-section">
        <h2 className="font-display font-bold text-lg mb-4">{t("parametres.language")}</h2>
        <LanguageSwitcher />
      </section>

      <section className="rounded-2xl border border-border bg-card p-7 space-y-5" data-testid="ai-style-section">
        <h2 className="font-display font-bold text-lg">{t("parametres.aiStyle")}</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">{t("parametres.tone")}</label>
            <div className="flex flex-wrap gap-1.5">
              {TONES.map((tone, i) => (
                <button key={tone} onClick={() => setSettings({ ...settings, tone })} data-testid={`settings-tone-${tone}`}
                  className={`rounded-full px-3.5 py-1.5 text-[11px] font-bold border transition-colors ${settings.tone === tone ? "bg-electric text-white border-electric" : "border-border hover:border-electric/50"}`}>
                  {toneLabels[i]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">{t("parametres.length")}</label>
            <div className="flex flex-wrap gap-1.5">
              {LENGTHS.map((l, i) => (
                <button key={l} onClick={() => setSettings({ ...settings, length: l })} data-testid={`settings-length-${l}`}
                  className={`rounded-full px-3.5 py-1.5 text-[11px] font-bold border transition-colors ${settings.length === l ? "bg-electric text-white border-electric" : "border-border hover:border-electric/50"}`}>
                  {lengthLabels[i]}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">{t("parametres.signature")}</label>
          <input value={settings.signature || ""} onChange={(e) => setSettings({ ...settings, signature: e.target.value })}
            placeholder={t("parametres.signaturePlaceholder")} data-testid="settings-signature" className={field} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">{t("parametres.useExp")}</label>
            <input value={useExp} onChange={(e) => setUseExp(e.target.value)} placeholder={t("parametres.useExpPlaceholder")} data-testid="settings-use-exp" className={field} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">{t("parametres.forbidExp")}</label>
            <input value={forbidExp} onChange={(e) => setForbidExp(e.target.value)} placeholder={t("parametres.forbidExpPlaceholder")} data-testid="settings-forbid-exp" className={field} />
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">{t("parametres.instructions")}</label>
          <textarea value={settings.custom_instructions || ""} onChange={(e) => setSettings({ ...settings, custom_instructions: e.target.value })}
            rows={3} placeholder={t("parametres.instructionsPlaceholder")} data-testid="settings-instructions"
            className={`${field} resize-none`} />
        </div>
        <button onClick={save} disabled={busy} data-testid="settings-save"
          className="rounded-full bg-electric text-white px-6 py-2.5 text-xs font-bold hover:bg-blue-700 transition-colors disabled:opacity-60 flex items-center gap-1.5">
          <Save size={13} /> {busy ? t("parametres.saving") : t("common.save")}
        </button>
      </section>

      <section className="rounded-2xl border border-border bg-card p-7" data-testid="mailboxes-section">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display font-bold text-lg">{t("parametres.mailboxes")}</h2>
          {mailboxes && <span className="text-[11px] text-muted-foreground">{t("parametres.usedOf", { used: mailboxes.mailboxes.length, limit: mailboxes.limit, plan: mailboxes.plan.toUpperCase() })}</span>}
        </div>
        <div className="space-y-2.5 mb-5">
          {(mailboxes?.mailboxes || []).map((mb) => (
            <div key={mb.mailbox_id} className="flex items-center gap-3 rounded-xl border border-border bg-background px-4 py-3" data-testid={`mailbox-${mb.provider}`}>
              <Mail size={15} className="text-electric shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{mb.email_address}</p>
                <p className="text-[10px] text-muted-foreground">{mb.provider === "demo" ? t("parametres.demoLabel") : mb.label}</p>
              </div>
              {mb.provider === "demo" && <span className="text-[9px] font-black bg-warning/15 text-warning rounded-full px-2 py-0.5">{t("common.demo")}</span>}
              <button onClick={() => disconnect(mb)} data-testid={`mailbox-disconnect-${mb.provider}`}
                className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/mailboxes/oauth/gmail/start`} data-testid="connect-gmail"
            className="rounded-full border border-border px-5 py-2.5 text-xs font-bold hover:border-electric/50 hover:text-electric transition-colors inline-block">
            {t("parametres.connectGmail")}
          </a>
          <button type="button" onClick={connectOutlook} data-testid="connect-outlook"
            className="rounded-full border border-border px-5 py-2.5 text-xs font-bold hover:border-electric/50 hover:text-electric transition-colors">
            {t("parametres.connectOutlook")}
          </button>
          {(mailboxes?.mailboxes || []).some((m) => m.provider !== "demo") && (
            <button onClick={syncNow} disabled={syncing} data-testid="sync-now"
              className="rounded-full bg-electric text-white px-5 py-2.5 text-xs font-bold hover:bg-blue-700 transition-colors disabled:opacity-60">
              {syncing ? t("parametres.syncing") : t("parametres.syncNow")}
            </button>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground mt-4 leading-relaxed">
          {t("parametres.oauthNote")}
        </p>
      </section>

      <section className="rounded-2xl border border-border bg-card p-7" data-testid="privacy-section">
        <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><ShieldCheck size={18} className="text-success" /> {t("parametres.privacyTitle")}</h2>
        <ul className="text-xs text-muted-foreground space-y-2 leading-relaxed">
          {t("parametres.privacy").map((item) => <li key={item}>· {item}</li>)}
        </ul>
      </section>
    </div>
  );
}
